import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpFunctionsService } from '../http-services/http-functions.service';
import { TrendingSellerModel, TrendingSellerPage } from 'src/app/models/trending-seller.model';

@Injectable({
  providedIn: 'root'
})
export class PlatformSaleService {
  endPoint: string = 'platform-sale/';
  endPointSaleBeat: string = "beat-sale/";
  bpmArenaEndPoint: string = "competitions/";
  constructor(private httpMethods: HttpFunctionsService) { }

  /**
   * Get active sale id 
   * @param isShowLoader false: no page loader (sale start / end refresh)
   * @returns 
   */
  getActivePlatFormSale(isShowLoader: boolean = true) {
    return this.httpMethods.getMethod(this.endPoint + 'active', isShowLoader);
  }

  /**
   * enroll the beat api 
   * @param data 
   * @returns 
   */
  enrollBeat(data: any) {
    return this.httpMethods.postMethod(this.endPointSaleBeat+"enroll", data);
  }

  /**
   * delete the Enrollment into the beat 
   * @param enrollmentID 
   * @returns 
   */
  deleteEnrollbeat(enrollmentID: any) {
    return this.httpMethods.deleteMethod(this.endPointSaleBeat + enrollmentID);
  }

  getEnrollmentList(isShowLoder: boolean = true) {
    return this.httpMethods.getMethod(this.endPointSaleBeat+"/my-enrollments", isShowLoder);
  }

  /**
   * Change the enrollment status
   * @param enrollmentId 
   * @param discountedTire 
   * @returns 
   */
  changeEnrollmentStatus(enrollmentId: any, discountedTire: any) {
    return this.httpMethods.putMethod(this.endPointSaleBeat + enrollmentId + "/discount", discountedTire);
  }

  getEnrollmentsBeats(sendData: any) {
    return this.httpMethods.postMethod(this.endPointSaleBeat + "list", sendData);
  }

  beatWithdrawSale(sendData: any) {
    return this.httpMethods.putMethod(this.endPointSaleBeat+"withdraw-sale", sendData);
  }

  /**
   * Get Active Completitions
   */
  getActiveCompetitions() {
    return this.httpMethods.getMethod(this.bpmArenaEndPoint+"active");
  }

  /**
   * Function For Express Entery
   * @param sendData 
   * @returns 
   */
  entrySubmited(sendData: any, actionType: string) {
    let url = actionType === 'normal' ? 'entry' : 'express-entry';
    return this.httpMethods.postMethod(this.bpmArenaEndPoint+url, sendData);
  }

  /**
   * 
   * @returns 
   */
  getMySubmittedEntries() {
    return this.httpMethods.getMethod(this.bpmArenaEndPoint+'my-entries');
  }

  getPlatFormStatsDetails(saleId: any, isShowLoader: boolean = true) {
    return this.httpMethods.getMethod(this.endPoint + saleId+"/stats", isShowLoader);
  }
  
  /**
   * @param isShowLoader
   * @param page offset / limit of the page to load (the Top Sellers page); without it the API's default list is returned
   */
  getTrendingSeller(isShowLoader: boolean = true, page?: { offset: number; limit: number }) {
    if (page) {
      return this.httpMethods.getMethodWithParams("producer/trending-sellers", { offset: page.offset.toString(), limit: page.limit.toString() }, isShowLoader);
    }
    return this.httpMethods.getMethod("producer/trending-sellers", isShowLoader);
  }

  /**
   * One page of the trending sellers (Top Sellers page): GET producer/trending-sellers?offset=&limit=.
   * More pages follow when the API says so (pagination.hasMore), else while a page comes back full.
   * @param offset where the page starts (first page 0)
   * @param limit sellers per page
   * @param isShowLoader false for the next pages (the page shows its own "loading more" line)
   */
  getTrendingSellerPage(offset: number = 0, limit: number = 20, isShowLoader: boolean = true): Observable<TrendingSellerPage> {
    return this.getTrendingSeller(isShowLoader, { offset, limit }).pipe(map((response: any) => {
      const sellerList: Array<any> = response?.data?.sellerBand || [];
      const pagination = response?.pagination || response?.data?.pagination;
      return {
        sellers: sellerList.map((item: any) => this.toTrendingSeller(item))
          .filter((seller: TrendingSellerModel | null): seller is TrendingSellerModel => !!seller),
        offset,
        limit,
        hasMore: typeof pagination?.hasMore === 'boolean' ? pagination.hasMore : sellerList.length >= limit,
      };
    }));
  }

  /**
   * Trending sellers in the API's band order (6-slot bands, Seller Listing Algorithm v3.0; the order is kept as is).
   * Empty slots (a pool ran out: the API sends the seller without a name) are left out, never backfilled.
   * @param isShowLoader
   * @returns
   */
  getTrendingSellerList(isShowLoader: boolean = true): Observable<Array<TrendingSellerModel>> {
    return this.getTrendingSeller(isShowLoader).pipe(map((response: any) => {
      const sellerList: Array<any> = response?.data?.sellerBand || [];
      console.log('PlatformSaleService: getTrendingSellerList response', response, 'sellerList', sellerList);
      return sellerList.map((item: any) => this.toTrendingSeller(item))
        .filter((seller: TrendingSellerModel | null): seller is TrendingSellerModel => !!seller);
    }));
  }

  /**
   * API band item -> TrendingSellerModel; null for an empty slot (no seller name).
   * Band items carry the seller fields flat (seller = id); a populated seller object is read too.
   */
  private toTrendingSeller(item: any): TrendingSellerModel | null {
    const seller: any = item?.seller && typeof item.seller === 'object' ? item.seller : {};
    const name: string = item?.storeName || item?.display_name || seller.store?.general?.name || seller.name || '';
    if (!name) {
      return null;
    }
    const count = (value: any): number | null => (value === undefined || value === null || isNaN(Number(value)) ? null : Number(value));
    const genre = item.primaryGenre ?? item.primary_genre ?? seller.primaryGenre;
    const handle: string = item.handle || seller.handle || seller.store?.general?.handle || '';
    const poolValue = Number(item.pool);
    const pool: 1 | 2 | 3 = poolValue === 1 || poolValue === 2 ? poolValue : 3;
    const badges: Array<string> = Array.isArray(item.badges) ? item.badges : (pool === 1 ? ['Pro'] : pool === 2 ? ['Premium'] : []);
    return {
      id: (typeof item.seller === 'string' ? item.seller : seller._id) || item.seller_id || '',
      // the profile route is /@handle
      handle: handle && !handle.startsWith('@') ? '@' + handle : handle,
      name,
      avatar: item.coverImage || item.avatar_url || seller.profile_picture || seller.store?.general?.logo || '',
      genre: typeof genre === 'string' ? genre.split(/[_-]/).filter(Boolean).map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '',
      followers: count(item.totalFollowers ?? seller.followers ?? seller.totalFollowers ?? seller.follower_count),
      likes: count(item.totalLikes ?? seller.likes ?? seller.totalLikes),
      plays: count(item.totalPlays ?? seller.plays ?? seller.totalPlays ?? seller.play_count),
      isFollowed: !!(item.isFollowed ?? seller.isFollowed ?? seller.is_followed),
      pool,
      activityScore: count(item.activityScore ?? item.activity_score),
      badges,
      isNew: !!(item.isNew ?? item.is_new),
      ctaType: item.ctaType || item.cta_type || '',
    };
  }

}
